import { useApolloClient } from '@apollo/client';
import { Divider, Form, message } from 'antd';
import { Formik, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { type MyInfoModuleSpecsFragment } from '../../../../api/fragments/MyInfoModuleSpecs';
import {
    CreateMyInfoSettingDocument,
    type CreateMyInfoSettingMutation,
    type CreateMyInfoSettingMutationVariables,
} from '../../../../api/mutations/createMyInfoSetting';
import { type MyInfoSettingInput, MyInfoSettingVersion } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import PasswordField from '../../../../components/fields/PasswordField';
import SelectField from '../../../../components/fields/SelectField';
import SingleSecretUploadField from '../../../../components/fields/SingleSecretUploadField';
import SwitchField from '../../../../components/fields/SwitchField';
import useHandleError from '../../../../utilities/useHandleError';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import useMyinfoVersionOptions from './useMyinfoVersionOptions';

export type AddMyInfoSettingsFormProps = {
    module: MyInfoModuleSpecsFragment;
    onComplete: () => void;
};

type FormValues = MyInfoSettingInput;

export const myInfoFormValidator = validators.compose(
    validators.requiredNonEmptyString('displayName'),
    validators.requiredString('version'),
    validators.requiredBoolean('isTestEnvironment'),
    validators.requiredString('clientId'),
    validators.requiredString('baseUrl'),
    validators.only(
        values => values?.version === MyInfoSettingVersion.MyInfoV3,
        validators.compose(
            validators.requiredString('requestPurpose'),
            validators.requiredString('clientSecret'),
            validators.requiredString('privateKey'),
            validators.requiredString('publicCertificate')
        )
    )
);

const initialValues: FormValues = {
    displayName: '',
    version: MyInfoSettingVersion.MyInfoV5,
    isTestEnvironment: false,
    clientId: '',
    requestPurpose: '',
    baseUrl: '',
    clientSecret: '',
    privateKey: '',
    publicCertificate: '',
    pkiSignature: true,
};

const FormFields = () => {
    const { t } = useTranslation(['moduleDetails', 'myInfoModuleDetails']);
    const { yesNoSwitch } = useSystemSwitchData();
    const versionOptions = useMyinfoVersionOptions();
    const {
        values: { version },
    } = useFormikContext<FormValues>();

    return (
        <>
            <InputField
                {...t('myInfoModuleDetails:myInfoSettingsDrawer.fields.displayName', {
                    returnObjects: true,
                })}
                name="displayName"
                required
            />
            <SelectField
                {...t('myInfoModuleDetails:myInfoSettingsDrawer.fields.version', {
                    returnObjects: true,
                })}
                name="version"
                options={versionOptions}
                showSearch
            />
            <SwitchField
                {...t('myInfoModuleDetails:myInfoSettingsDrawer.fields.isTestEnvironment', { returnObjects: true })}
                name="isTestEnvironment"
                {...yesNoSwitch}
            />
            <InputField
                {...t('myInfoModuleDetails:myInfoSettingsDrawer.fields.clientId', {
                    returnObjects: true,
                })}
                name="clientId"
            />
            <InputField
                {...t('myInfoModuleDetails:myInfoSettingsDrawer.fields.baseUrl', {
                    returnObjects: true,
                })}
                name="baseUrl"
            />
            {version === MyInfoSettingVersion.MyInfoV3 && (
                <>
                    <InputField
                        {...t('myInfoModuleDetails:myInfoSettingsDrawer.fields.requestPurpose', {
                            returnObjects: true,
                        })}
                        name="requestPurpose"
                    />
                    <PasswordField
                        {...t('myInfoModuleDetails:myInfoSettingsDrawer.fields.clientSecret', { returnObjects: true })}
                        name="clientSecret"
                    />
                    <SingleSecretUploadField
                        {...t('myInfoModuleDetails:myInfoSettingsDrawer.fields.privateKey', { returnObjects: true })}
                        name="privateKey"
                    />
                    <SingleSecretUploadField
                        {...t('myInfoModuleDetails:myInfoSettingsDrawer.fields.publicCertificate', {
                            returnObjects: true,
                        })}
                        name="publicCertificate"
                    />
                    <SwitchField
                        {...t('myInfoModuleDetails:myInfoSettingsDrawer.fields.pkiSignature', { returnObjects: true })}
                        name="pkiSignature"
                        {...yesNoSwitch}
                    />
                </>
            )}
        </>
    );
};

const AddMyInfoSettingsForm = ({ module, onComplete }: AddMyInfoSettingsFormProps) => {
    const { t } = useTranslation(['moduleDetails', 'myInfoModuleDetails']);
    const { id: myInfoModuleId } = module;

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('myInfoModuleDetails:myInfoSettingsDrawer.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<CreateMyInfoSettingMutation, CreateMyInfoSettingMutationVariables>({
                    mutation: CreateMyInfoSettingDocument,
                    variables: { myInfoModuleId, secrets: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('myInfoModuleDetails:myInfoSettingsDrawer.messages.success'),
                key: 'primary',
            });

            // close the drawer
            onComplete();
        },
        [onComplete, t, apolloClient, myInfoModuleId]
    );

    const validate = useValidator(myInfoFormValidator);

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form id="addMyInfoSettings" layout="vertical" name="addMyInfoSettings" onSubmitCapture={handleSubmit}>
                    <Divider>{t('myInfoModuleDetails:dividers.myInfoSettings')}</Divider>
                    <FormFields />
                </Form>
            )}
        </Formik>
    );
};

export default AddMyInfoSettingsForm;
